/**
 * Slider Component implemented with Material UI
 * 
 * This component uses MUI's Switch component to render a toggle switch
 * with customizable styling and Redux integration.
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Switch, FormControlLabel } from '@mui/material';
import ReducersIndex from 'reduxModules/ducks';
import { getCurrentValue2 as getContainerValue } from 'reduxModules/selectors/containerSelectors';
import InputLabel from './InputLabel';
import { SliderProps } from 'types/components/atoms';
import { RootState } from 'types/redux/state';
import { GetCurrentValue2 } from 'types/redux/selectors';
import { AnyAction } from '@reduxjs/toolkit';

// Type assertion for the imported getCurrentValue2 function
const getCurrentValue2 = getContainerValue as GetCurrentValue2;

/**
 * Slider component
 * 
 * @param props - Component props
 * @returns The Switch component with custom styling
 */
const Slider: React.FC<SliderProps> = ({
  name,
  label,
  dispatcher,
  animate,
  isNav,
  className,
  ...otherProps
}) => {  
  // Get the current value from Redux
  const currentValue = useSelector((state: RootState) => {
    if (!dispatcher) return false;
    // Ensure proper parameters are passed to getCurrentValue2 function
    return getCurrentValue2(state, dispatcher, name || '');
  });
  
  // Local state for the checked status
  const [checked, setChecked] = useState<boolean>(false);
  
  // Update local state when Redux state changes
  useEffect(() => {
    setChecked(!!currentValue);
  }, [currentValue]);
  
  // Redux dispatch
  const dispatch = useDispatch();
  
  // Handle click event
  const handleClick = () => {
    if (dispatcher) {
      const newValue = name ? { ...{}, [name]: !checked } : !checked;
      if (dispatcher in ReducersIndex.container) {
        dispatch(ReducersIndex.container[dispatcher](newValue) as AnyAction);
      }
      // The below dispatcher is for add another area flag which is UI only
      if (dispatcher in ReducersIndex.uiReducers) {
        dispatch(ReducersIndex.uiReducers[dispatcher](newValue) as AnyAction);
      }
    }
    setChecked(!checked);
  };
  
  // Custom styles to match the original component
  const switchStyles = {
    '& .MuiSwitch-switchBase': {
      color: 'var(--app-medium-gray)',
      '&.Mui-checked': {
        color: '#fff',
      },
      '&.Mui-checked + .MuiSwitch-track': {
        backgroundColor: 'var(--app-red)',
      },
    },
    '& .MuiSwitch-track': {
      backgroundColor: 'var(--app-light-gray)',
      border: '1px solid var(--app-dark-gray)',
    },
    '& .MuiSwitch-thumb': {
      width: 20,
      height: 20,
    },
    width: 50,
    height: isNav ? 26 : 28,
  };
  
  return (
    <Box className={`d-flex flex-wrap gap-1 ${className || ''}`}>
      {label ? (
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              onChange={handleClick}
              sx={switchStyles}
              disableRipple={!animate}
            />
          }
          label={<InputLabel>{label}</InputLabel>}
          labelPlacement="start"
        />
      ) : (
        <Switch
          checked={checked}
          onChange={handleClick}
          sx={switchStyles}
          disableRipple={!animate}
        />
      )}
    </Box>
  );
};

export type { SliderProps };
export default Slider;
